import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter, switchMap } from 'rxjs/operators';
import { SitesService } from '../../core/services/data/sites.service';
import { SiteAdvertsService } from '../../core/services/data/site-adverts.service';
import { UtilitiesService } from '../../core/services/utilities.service';
import { FeaturedBooksService } from '../../core/services/data/featured-books.service';
import { AnalyticsService } from '../../core/services/analytics.service';
import { BooksService } from '../../core/services/data/books.service';
import { UserService } from '../../core/services/data/user.service';
import { SettingsService } from '../../core/services/data/settings.service';
import { OrderService } from '../../core/services/data/order.service';
import { ModalService } from '../../core/services/modal.service';
import { PurchaseProcessService } from '../../core/services/data/purchase-process.service';
import { ISite } from '../../types/datatypes';
import { ExperimentsService } from '../../core/services/experiments.service';
import { LocalStorageService } from '../../core/services/data/local-storage.service';

@Component({
	templateUrl: './features.component.html',
	styleUrls: ['./features.component.scss'],
})
export class FeaturesComponent implements OnInit, OnDestroy {
	public orderCheckoutId;

	public orderEditingId;

	public cartPurchaseEnabled = true;

	public showTopBar = true;

	public purchaseSource = 'Standard';

	public user;

	public displayPromoStacksModalForFeatures = false;

	public screenWidth;

	public displayPromoStacksModal = true;

	public displayTopbarGuest = false;

	public variant = 'control';

	private order$;

	private order;

	private orderEditingId$;

	private initializer$;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private location: Location,
		private fbs: FeaturedBooksService,
		private bs: BooksService,
		private ss: SitesService,
		private sas: SiteAdvertsService,
		private us: UtilitiesService,
		private as: AnalyticsService,
		private uss: UserService,
		private sts: SettingsService,
		private os: OrderService,
		private ms: ModalService,
		public purchaseProcessService: PurchaseProcessService,
		public experimentsService: ExperimentsService,
		private localStorageService: LocalStorageService,
	) {
		this.uss.fetchUser().then((data: any) => {
			this.user = data;
			this.purchaseProcessService.setUser(data);
			if (!this.router.url.includes('/author-dashboard/')) {
				this.showTopBar = false;
			} else {
				this.showTopBar = true;
			}
			this.displayTopbarGuest = false;
		}, () => {
			this.user = null;
			this.showTopBar = false;
			this.displayTopbarGuest = true;
		});
	}

	get currentStep() {
		return this.purchaseProcessService.currentStep;
	}

	get site(): ISite {
		return this.purchaseProcessService.site;
	}

	get siteAdvertSlug() {
		return this.purchaseProcessService.siteAdvertSlug;
	}

	get isCheckout() {
		return this.purchaseProcessService.isCheckout;
	}

	canDeactivate() {
		// Don't clear the saved book data if an item is being edited in the cart
		// if (!this.orderEditingId) {
		// 	this.bs.clearData();
		// }
		return true;
	}

	ngOnInit() {
		this.checkVariant();

		this.route.params.subscribe(({ authorLink }) => {
			this.purchaseProcessService.setAuthorLink(authorLink);
		});

		this.route.firstChild.params.subscribe((params: any) => {
			this.purchaseProcessService.setState(params, this.router.url.includes('order-checkout'));
		});

		this.initializer$ = this.router.events.pipe(
			filter((event) => event instanceof NavigationEnd),
			switchMap(() => this.route.firstChild?.params),
		).subscribe((params: any) => {
			this.purchaseProcessService.setState(params, this.router.url.includes('order-checkout'));
		});

		this.order$ = this.os.orderStore$.subscribe((order) => {
			this.order = order;
		});
		this.orderEditingId$ = this.os.editingItemId$.subscribe((value) => {
			if (value && this.order?.items?.length) {
				const orderEditingFeature = this.order.items.find(({ itemId }) => itemId === value);
				if (orderEditingFeature && orderEditingFeature.itemId) {
					this.orderEditingId = orderEditingFeature.itemId;
				}
			} else {
				this.orderEditingId = null;
			}
		});

		this.sts.fetchCartPurchaseSetting().then(
			(value) => {
				this.cartPurchaseEnabled = !!value;
			},
		);

		this.sts.displayPromoStacksModalForFeatures().then(
			(value) => {
				this.displayPromoStacksModalForFeatures = !!value;

				if (this.currentStep === 1 && this.displayPromoStacksModalForFeatures && this.variant === 'control') {
					setTimeout(() => {
						this.initPromoStacksModal();
					}, 0);
				}
			},
		);

		this.checkCookies();
	}

	ngOnDestroy() {
		this.order$?.unsubscribe();
		this.initializer$?.unsubscribe();
	}

	handleCartComplete() {
		// This might be coincidental positioning in the router params...
		this.orderCheckoutId = this.siteAdvertSlug;
	}

	handleCheckout() {
		this.orderCheckoutId = this.siteAdvertSlug;
	}

	generateDateLabelFromRange(range, siteName) {
		let d = range;

		if (this.ss.isFacebookOrAmazonAdSite(siteName)) {
			// If no dash, then it's a single date and still handled correctly here.
			const pubDateWindow = range.split('-');
			[d] = pubDateWindow;
		}

		return this.us.momentInstance(d, 'YYYYMMDD').format('ddd, MMM D, YYYY');
	}

	isInactiveCurrentStep(step) {
		return !step.active || this.purchaseProcessService.currentStep === step.step;
	}

	currentStepClass(step) {
		if (this.purchaseProcessService.currentStep === step.step) return 'selected';
		return '';
	}

	isActiveStep(step) {
		return step.active && this.purchaseProcessService.currentStep !== step.step;
	}

	@HostListener('window:resize', ['$event'])
	initPromoStacksModal() {
		if (this.isCheckout) return;
		this.screenWidth = window.innerWidth;

		if (this.user && this.screenWidth >= 1201 && this.displayPromoStacksModal && this.displayPromoStacksModalForFeatures) {
			this.ms.open('stacks-modal');
		} else if (!this.user && this.screenWidth >= 1021 && this.displayPromoStacksModal && this.displayPromoStacksModalForFeatures) {
			this.ms.open('stacks-modal');
		} else {
			this.ms.close('stacks-modal');
		}

		if (this.displayPromoStacksModal && this.displayPromoStacksModalForFeatures) {
			const body = document.getElementsByTagName('body')[0];
			if (body) {
				body.classList.remove('modal-open');
			}
		}
	}

	checkVariant() {
		this.variant = this.experimentsService.getVariant();
	}

	checkCookies() {
		const sailthruBid = this.getCookie('sailthru_bid');

		if (sailthruBid) {
			this.localStorageService.saveData('messageId', sailthruBid);
		}
	}

	getCookie(name) {
		const value = `; ${document.cookie}`;
		const parts = value.split(`; ${name}=`);
		if (parts.length === 2) return parts.pop().split(';').shift();
		return null;
	}
}
